import React, { useState } from "react";
import {
  Modal,
  Button,
  Form,
  Table,
  Spinner,
  Row,
  Col,
  Alert,
  Dropdown,
} from "react-bootstrap";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useAuth } from "../../hooks/useAuth";
import Select from "react-select";
import DotsVerticalIcon from "mdi-react/DotsVerticalIcon";
import {
  EditIcon,
  DeleteIcon,
  FileListIcon,
  DropdownEyeIcon,
  DropdownCheckIcon,
} from "../Icons";
import { formatDate } from "../../utils/helpers";
import FillFormModal from "../Requisition/FillFormModal";
import CachedIcon from "mdi-react/CachedIcon";
import DateTime from "react-datetime";
import FormPreviewModal from "../Requisition/FormPreviewModal";

const UserCertification = ({ show = true, onHide = () => {} }) => {
  const { backendUrl, token } = useAuth();

  const [formData, setFormData] = useState({
    Staff_ID: "",
    assetRequirementId: "",
    expiresAt: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [actionError, setActionError] = useState("");
  const [selectedFormToFill, setSelectedFormToFill] = useState(null);
  const [showFormPreviewModal, setShowFormPreviewModal] = useState(false);
  const [selectedFilledForm, setSelectedFilledForm] = useState(null);
  const queryClient = useQueryClient();

  const extractResponseData = payload => {
    if (payload && typeof payload === "object" && "data" in payload) {
      return payload.data;
    }
    return payload;
  };

  const normalizeArrayPayload = payload => {
    if (Array.isArray(payload)) return payload;
    if (payload && typeof payload === "object") {
      if (Array.isArray(payload.data)) return payload.data;
      if (payload.data && Array.isArray(payload.data.assetRequirements))
        return payload.data.assetRequirements;
      if (payload.data && Array.isArray(payload.data.rows))
        return payload.data.rows;
      if (Array.isArray(payload.assetRequirements))
        return payload.assetRequirements;
      if (Array.isArray(payload.rows)) return payload.rows;
    }
    return [];
  };

  const toDatetimeLocalValue = value => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const timezoneOffsetMs = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - timezoneOffsetMs)
      .toISOString()
      .slice(0, 16);
  };

  // Fetch staff/users for the staff dropdown
  const fetchStaff = async () => {
    const response = await fetch(`${backendUrl}/api/users`, {
      method: "GET",
      headers: {
        Accept: "Application/json",
        "Content-Type": "Application/json",
        Authorization: `Bearer ${token}`,
      },
      credentials: "include",
    });
    if (!response.ok) throw new Error("Failed to fetch staff.");
    const data = await response.json();
    return (data.staff || []).map(el => ({
      label: el.Name,
      value: el.Staff_ID,
    }));
  };

  // Fetch asset-requirement entries (created in AssetCertification)
  const fetchAssetRequirements = async () => {
    const response = await fetch(`${backendUrl}/api/asset-requirements`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      credentials: "include",
    });
    if (!response.ok) throw new Error("Failed to fetch asset requirements.");
    const payload = await response.json();
    return normalizeArrayPayload(payload);
  };

  // Fetch certification types for readable labels
  const fetchCertificationTypes = async () => {
    const response = await fetch(
      `${backendUrl}/api/partnership/certification-types`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (!response.ok) throw new Error("Failed to fetch certification types.");
    const payload = await response.json();
    return normalizeArrayPayload(payload);
  };

  const fetchUserCertifications = async () => {
    const response = await fetch(`${backendUrl}/api/user-certifications`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) throw new Error("Failed to fetch user certifications.");
    const payload = await response.json();
    const data = extractResponseData(payload);
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.userCertifications))
      return data.userCertifications;
    return [];
  };

  const {
    data: certifications = [],
    isLoading,
    isFetching,
    error,
    refetch: refetchCertifications,
  } = useQuery(["user-certifications"], fetchUserCertifications, {
    enabled: show,
    refetchOnWindowFocus: false,
  });

  const {
    data: staffOptions = [],
    isLoading: isLoadingStaff,
    refetch: refetchStaff,
  } = useQuery(["staff-users"], fetchStaff, {
    enabled: show,
    refetchOnWindowFocus: false,
  });

  const {
    data: assetRequirements = [],
    isLoading: isLoadingAssetReqs,
    refetch: refetchAssetReqs,
  } = useQuery(["asset-certifications"], fetchAssetRequirements, {
    enabled: show,
    refetchOnWindowFocus: false,
  });

  const { data: certificationTypes = [], refetch: refetchCertTypes } = useQuery(
    ["certification-types"],
    fetchCertificationTypes,
    {
      enabled: show,
      refetchOnWindowFocus: false,
    }
  );

  const refetchAll = () => {
    refetchCertifications();
    refetchStaff();
    refetchAssetReqs();
    refetchCertTypes();
  };

  // Build dropdown options from asset-requirement entries
  const getCertLabel = certificationId => {
    const ct = certificationTypes.find(
      t =>
        String(t.certificationId || t.id || t.code || t.name) ===
        String(certificationId)
    );
    return (
      ct?.certificationName || ct?.name || ct?.label || certificationId || ""
    );
  };

  const assetReqOptions = assetRequirements.map(ar => ({
    value: ar.id,
    label: `${ar.Bar_Code} — ${getCertLabel(ar.certificationId)}`,
  }));

  const createMutation = useMutation(
    async payload => {
      const response = await fetch(`${backendUrl}/api/user-certifications`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("Failed to create user certification.");
      return extractResponseData(await response.json());
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["user-certifications"]);
        resetForm();
        setActionError("");
      },
      onError: mutationError => {
        setActionError(
          mutationError?.message || "Could not create user certification."
        );
      },
    }
  );

  const updateMutation = useMutation(
    async ({ id, payload }) => {
      const response = await fetch(
        `${backendUrl}/api/user-certifications/${id}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) throw new Error("Failed to update user certification.");
      return extractResponseData(await response.json());
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["user-certifications"]);
        resetForm();
        setActionError("");
      },
      onError: mutationError => {
        setActionError(
          mutationError?.message || "Could not update user certification."
        );
      },
    }
  );

  const deleteMutation = useMutation(
    async id => {
      const response = await fetch(
        `${backendUrl}/api/user-certifications/${id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to delete user certification.");
      return id;
    },
    {
      onMutate: id => {
        setDeletingId(id);
      },
      onSuccess: () => {
        queryClient.invalidateQueries(["user-certifications"]);
        setActionError("");
      },
      onError: mutationError => {
        setActionError(
          mutationError?.message || "Could not delete user certification."
        );
      },
      onSettled: () => {
        setDeletingId(null);
      },
    }
  );

  const approveMutation = useMutation(
    async ({ id, isApproved }) => {
      const response = await fetch(
        `${backendUrl}/api/user-certifications/${id}/approve`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ isApproved }),
        }
      );
      if (!response.ok) throw new Error("Failed to update approval status.");
      return extractResponseData(await response.json());
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["user-certifications"]);
        setActionError("");
      },
      onError: mutationError => {
        setActionError(
          mutationError?.message || "Could not update approval status."
        );
      },
    }
  );

  const handleApprove = (id, currentStatus) => {
    approveMutation.mutate({ id, isApproved: !currentStatus });
  };

  const handleCreate = async e => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  const handleUpdate = async id => {
    updateMutation.mutate({ id, payload: formData });
  };

  const handleDelete = async id => {
    deleteMutation.mutate(id);
  };

  const handleEdit = cert => {
    setEditingId(cert.id);
    setFormData({
      Staff_ID: cert.Staff_ID,
      assetRequirementId: cert.assetRequirementId,
      expiresAt: toDatetimeLocalValue(cert.expiresAt),
    });
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({ Staff_ID: "", assetRequirementId: "", expiresAt: "" });
    setActionError("");
  };

  const isSubmitting = createMutation.isLoading || updateMutation.isLoading;
  const isDeleting = deleteMutation.isLoading;
  const isApproving = approveMutation.isLoading;

  const selectedStaffOption =
    staffOptions.find(
      option => String(option.value) === String(formData.Staff_ID)
    ) || null;

  const selectedAssetReqOption =
    assetReqOptions.find(
      option => String(option.value) === String(formData.assetRequirementId)
    ) || null;

  // Resolve display labels for table rows
  const getStaffName = staffId => {
    const match = staffOptions.find(o => String(o.value) === String(staffId));
    return match?.label || staffId;
  };

  const getAssetReqLabel = assetReqId => {
    const match = assetReqOptions.find(
      o => String(o.value) === String(assetReqId)
    );
    return match?.label || assetReqId;
  };

  // Resolve the formId from the certification type linked through the asset requirement
  const getFormIdForCert = cert => {
    const assetReq = assetRequirements.find(
      ar => String(ar.id) === String(cert.assetRequirementId)
    );
    if (!assetReq) return null;
    const certType = certificationTypes.find(
      t =>
        String(t.certificationId || t.id || t.code || t.name) ===
        String(assetReq.certificationId)
    );
    return certType?.formId || certType?.form_id || certType?.FormID || null;
  };

  const saveFilledForm = async ({ id: filledFormId }) => {
    try {
      await updateMutation.mutateAsync({
        id: selectedFormToFill.userCertId,
        payload: { filledFormId },
      });
    } catch (error) {
      console.error("Error saving filled form ID to certification:", error);
    }
  };

  const handleViewFilledForm = async filledFormId => {
    try {
      const response = await fetch(
        `${backendUrl}/api/forms/get-filled-form/${filledFormId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          credentials: "include",
        }
      );
      if (!response.ok) throw new Error("Failed to fetch filled form.");
      const payload = await response.json();
      const form = payload?.data?.form || payload;
      setSelectedFilledForm(form);
      setShowFormPreviewModal(true);
    } catch (error) {
      setActionError(error?.message || "Could not load filled form.");
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size="xl"
        centered
        scrollable
        fullscreen
        animation={false}
        enforceFocus={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className="h5">Staff Certification</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form
            onSubmit={
              editingId
                ? e => {
                    e.preventDefault();
                    handleUpdate(editingId);
                  }
                : handleCreate
            }
            className="mb-4"
          >
            {!!actionError && (
              <Alert variant="danger" className="mb-3">
                {actionError}
              </Alert>
            )}

            <Row className="g-3 align-items-end">
              <Col md={4}>
                <Form.Group controlId="userCertificationStaffId">
                  <Form.Label>Staff Member</Form.Label>
                  <Select
                    options={staffOptions}
                    value={selectedStaffOption}
                    onChange={option =>
                      setFormData({
                        ...formData,
                        Staff_ID: option?.value || "",
                      })
                    }
                    placeholder="Select staff member"
                    isClearable
                    isLoading={isLoadingStaff}
                    isDisabled={isSubmitting || isLoadingStaff}
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group controlId="userCertificationAssetReq">
                  <Form.Label>Asset Requirement</Form.Label>
                  <Select
                    options={assetReqOptions}
                    value={selectedAssetReqOption}
                    onChange={option =>
                      setFormData({
                        ...formData,
                        assetRequirementId: option?.value || "",
                      })
                    }
                    placeholder="Select asset requirement"
                    isClearable
                    isLoading={isLoadingAssetReqs}
                    isDisabled={isSubmitting || isLoadingAssetReqs}
                  />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group controlId="userCertificationExpiresAt">
                  <Form.Label>Expires At</Form.Label>
                  <DateTime
                    timeFormat={false}
                    closeOnSelect={true}
                    closeOnClickOutside={true}
                    dateFormat="MMM DD, YYYY"
                    inputProps={{
                      className: `date-input form-control`,
                      placeholder: "Select date",
                      readOnly: true,
                    }}
                    value={
                      formData.expiresAt ? new Date(formData.expiresAt) : null
                    }
                    onChange={date => {
                      setFormData({
                        ...formData,
                        expiresAt: date ? new Date(date).toISOString() : "",
                      });
                    }}
                  />
                </Form.Group>
              </Col>

              <Col md={1} className="d-grid">
                <Button type="submit" variant="primary" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <Spinner as="span" animation="border" size="sm" />
                  ) : editingId ? (
                    "Update"
                  ) : (
                    "Add"
                  )}
                </Button>
              </Col>
            </Row>

            {editingId && (
              <div className="mt-3">
                <Button
                  type="button"
                  variant="outline-primary"
                  onClick={resetForm}
                  disabled={isSubmitting}
                >
                  Cancel Edit
                </Button>
              </div>
            )}
          </Form>

          {isLoading ? (
            <div className="d-flex align-items-center gap-2">
              <Spinner animation="border" />
              <span>Loading staff certifications...</span>
            </div>
          ) : error ? (
            <Alert variant="danger" className="mb-0">
              {error?.message || "Error loading staff certifications."}
            </Alert>
          ) : (
            <>
              {/* {isFetching && (
              <div className="d-flex align-items-center gap-2 mb-3">
                <Spinner animation="border" size="sm" />
                <span>Refreshing data...</span>
              </div>
            )} */}

              <Table
                striped
                borderless
                hover
                responsive
                className="product-table mb-0"
              >
                <thead>
                  <tr>
                    <th>Staff</th>
                    <th>Asset Requirement</th>
                    <th>Status</th>
                    <th>Expires At</th>
                    <th>Created At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {certifications.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">
                        No user certifications found.
                      </td>
                    </tr>
                  )}

                  {certifications.map(cert => (
                    <tr key={cert.id}>
                      <td>{getStaffName(cert.Staff_ID)}</td>
                      <td>{getAssetReqLabel(cert.assetRequirementId)}</td>
                      <td>
                        <span
                          className={`badge bg-${
                            cert.isApproved ? "success" : "warning text-dark"
                          }`}
                        >
                          {cert.isApproved ? "Approved" : "Pending"}
                        </span>
                      </td>
                      <td>{formatDate(cert.expiresAt).toLocaleString()}</td>
                      <td>{formatDate(cert.created_at).toLocaleString()}</td>
                      <td>
                        <Dropdown>
                          <Dropdown.Toggle
                            variant=""
                            className="bg-white border-0"
                            bsPrefix="print more"
                          >
                            <DotsVerticalIcon />
                          </Dropdown.Toggle>

                          <Dropdown.Menu
                            popperConfig={{ strategy: "fixed" }}
                            renderOnMount
                            className="dropdown-with-icons"
                          >
                            {getFormIdForCert(cert) && !cert.filledFormId && (
                              <Dropdown.Item
                                as="button"
                                onClick={() =>
                                  setSelectedFormToFill({
                                    id: getFormIdForCert(cert),
                                    userCertId: cert.id,
                                  })
                                }
                              >
                                <FileListIcon />
                                Fill Form
                              </Dropdown.Item>
                            )}

                            {cert.filledFormId && (
                              <Dropdown.Item
                                as="button"
                                onClick={() =>
                                  handleViewFilledForm(cert.filledFormId)
                                }
                              >
                                <DropdownEyeIcon />
                                View Form
                              </Dropdown.Item>
                            )}

                            <Dropdown.Item
                              as="button"
                              onClick={() =>
                                handleApprove(cert.id, cert.isApproved)
                              }
                            >
                              <DropdownCheckIcon />
                              {cert.isApproved ? "Unapprove" : "Approve"}
                            </Dropdown.Item>

                            <Dropdown.Item
                              as="button"
                              onClick={() => handleEdit(cert)}
                            >
                              <EditIcon />
                              Edit
                            </Dropdown.Item>

                            <Dropdown.Item
                              as="button"
                              onClick={() => handleDelete(cert.id)}
                            >
                              <DeleteIcon />
                              Delete
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={refetchAll}
            disabled={isFetching}
          >
            <CachedIcon /> {isFetching ? "Refreshing..." : "Refresh"}
          </Button>
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {selectedFormToFill && (
        <FillFormModal
          openFilledForm={selectedFormToFill}
          setOpenFilledForm={setSelectedFormToFill}
          name={""}
          setthefilledForm={() => refetchCertifications()}
          mainFormID={selectedFormToFill?.id}
          onFilledFormSuccess={saveFilledForm}
        />
      )}

      {showFormPreviewModal && selectedFilledForm ? (
        <FormPreviewModal
          showFormPreviewModal={showFormPreviewModal}
          setShowFormPreviewModal={setShowFormPreviewModal}
          template={selectedFilledForm.filledFormData}
          selectedFilledForm={selectedFilledForm}
          setSelectedFilledForm={setSelectedFilledForm}
        />
      ) : null}
    </>
  );
};

export default UserCertification;
